import React, { useCallback, useEffect, useState } from 'react'
import { useContext } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  getCurriculumLevelsAnalytics,
  getMarkingStudentDataForLearningGrowth,
  getMarkingStudentDataForLearningGrowthRange,
  getMarkingStudentDataForLearningGrowthUnitOflearning,
  getMarkingStudentDataForLearningMatrix
} from '../../../../../../services/maestro/maestro.service'
import MarkingUtils from '../../../../../../utils/marking.utils'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import isEmpty from 'lodash/isEmpty'
import useFetchData from '../../../../../../hooks/useFetchData'
import { updateInputFilter } from '../../../../../../store/actions/filter.actions'
import { useSnackbar } from 'notistack'
import { handlerErrorV2 } from '../../../../../../utils/errors'

const ByStudentContext = React.createContext()

export const ByStudentProvider = ({ children }) => {
  const { studentId } = useParams()
  const navigate = useNavigate()
  const { pathname, state: stateLocation } = useLocation()

  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()

  const [curriculumId, setCurriculumId] = useState(null)
  const [currentCurriculum, setCurrentCurriculum] = useState({})
  const [studentSelected, setStudent] = useState(null)
  const [loading, setLoading] = useState(false)
  const [learningMatrix, setLearningMatrix] = useState([])
  const [dataChart, setDataChart] = useState([])
  const [byUL, setByUL] = useState([])

  const curriculumList = useSelector((state) => state?.assessment?.curriculum)
  const [viewLG, setViewLG] = useState(false)
  const { isLoading: isLoadingUser, data: user } = useFetchData('auth/profile')

  const onCurriculumChange = useCallback((value) => {
    setCurriculumId(value)
    setCurrentCurriculum(curriculumList.find((item) => item._id === value) || {})
  }, [])

  const setStudentSelected = useCallback(
    (student) => {
      setStudent(student)
      dispatch(updateInputFilter('learningMatrix', 'student', student))
      navigate(pathname, {
        state: {
          ...stateLocation,
          student
        }
      })
    },
    [stateLocation, pathname]
  )

  const loadStudent = useCallback(async () => {
    if (isEmpty(stateLocation?.student)) {
      if (isEmpty(studentSelected) && user) {
        if (user.roles.some((role) => role.id === 'Student')) {
          setStudentSelected({
            ...user,
            _id: user._id || user.userId,
            studentId: user.userId
          })
        }
        if (user.roles.some((role) => role.id === 'Parent')) {
          if (user.children?.length > 0) {
            const student = user.children.find((child) => child._id === studentId)
            if (student) {
              setStudentSelected(student)
            }
          }
        }
      }
    } else {
      if (isEmpty(studentSelected)) {
        setStudentSelected(stateLocation?.student)
      }
    }
  }, [user, studentSelected, stateLocation])

  useEffect(() => {
    loadStudent()
  }, [loadStudent])

  useEffect(() => {
    if (!curriculumId && curriculumList?.length > 0) {
      onCurriculumChange(curriculumList[0]._id)
    }
  }, [curriculumList])

  const onLoadLearningMatrix = async (curriculumId, studentSelected, filter = {}) => {
    setLoading(true)
    setLearningMatrix([])
    try {
      const response = await getMarkingStudentDataForLearningMatrix(curriculumId, studentSelected._id, {
        classId: filter.classId,
        ulId: filter.ulId,
        curriculumElements: filter.curriculumElements,
        lcElements: filter.lcElements
      })
      if (!response.data || response.error) {
        console.error(response.error)
        return
      } else {
        let studentData = MarkingUtils.mapGranularStepsLearningMatrix(
          currentCurriculum,
          studentSelected,
          response.data
        )
        setLearningMatrix(studentData)
      }
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const onLoadLearningGrowth = async (curriculumId, studentSelected, filter = {}) => {
    setLoading(true)
    setDataChart([])
    setLearningMatrix({ lcs: [] })
    try {
      const responseLearningMatrix = await getMarkingStudentDataForLearningMatrix(
        curriculumId,
        studentSelected._id,
        {
          classId: ['main'],
          ulId: ['main'],
          curriculumElements: filter.curriculumElements,
          lcElements: filter.lcElements
        }
      )
      if (!responseLearningMatrix.data || responseLearningMatrix.error) {
        console.error(responseLearningMatrix.error)
        return
      } else {
        const response = await getMarkingStudentDataForLearningGrowthRange(
          curriculumId,
          studentSelected._id,
          {
            classId: ['main'],
            ulId: ['main'],
            curriculumElements: filter.curriculumElements,
            lcElements: filter.lcElements,
            startDate: filter.startDate,
            endDate: filter.endDate,
            onlyLevel: true
          }
        )
        if (!response.data || response.error) {
          console.error(response.error)
          return
        } else {
          setDataChart(response.data)
          let studentData = MarkingUtils.mapGranularStepsLearningMatrix(
            currentCurriculum,
            studentSelected,
            responseLearningMatrix.data
          )
          const lcs = studentData.lcs.map((lc) => {
            let lgType = 'not-yet-assessed'
            const lg = response?.data?.filter((item) => item.lcId === lc._id && lc.ulId === item.ulId)

            if (lg.length > 0) {
              const first = lg[0]
              const last = lg[lg.length - 1]
              if (last.levelPosition === first.levelPosition) {
                lgType = 'steady'
              } else if (last.levelPosition > first.levelPosition) {
                lgType = 'growing'
              } else if (last.levelPosition < first.levelPosition) {
                lgType = 'needs-review'
              }
            }
            return {
              ...lc,
              lgType
            }
          })
          studentData.lcs = lcs
          setLearningMatrix(studentData)
        }
      }
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const onLoadLearningGrowthColumn = async (curriculumId, studentSelected, filter = {}) => {
    setLoading(true)
    setDataChart([])
    try {
      const response = await getMarkingStudentDataForLearningGrowth(curriculumId, studentSelected._id, {
        classId: filter.classId,
        ulId: filter.ulId,
        curriculumElements: filter.curriculumElements,
        lcElements: filter.lcElements
      })
      if (!response.data || response.error) {
        console.error(response.error)
        return
      } else {
        setDataChart(response.data)
      }
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const onLoadCurriculumLevels = async (curriculumId, studentSelected, filter = {}) => {
    setLoading(true)
    setDataChart([])
    try {
      const response = await getCurriculumLevelsAnalytics(curriculumId, studentSelected._id, filter)
      if (!response.data || response.error) {
        enqueueSnackbar(response.error.message || 'An error ocurred', {
          variant: 'error'
        })
        return
      } else {
        const data = response.data

        setDataChart({
          // data: data?.data || [],
          data: data?.data?.[0] ? [{ ...data?.data[0] }] : [] || [],
          types: data?.types || []
        })
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      enqueueSnackbar(message, {
        variant: 'error'
      })
    } finally {
      setLoading(false)
    }
  }

  const onLoadByUL = async (curriculumId, studentSelected, filter = {}) => {
    setLoading(true)
    setByUL([])

    try {
      const response = await getMarkingStudentDataForLearningGrowthUnitOflearning(
        curriculumId,
        studentSelected._id,
        {
          curriculumElements: filter.curriculumElements,
          lcElements: filter.lcElements,
          startDate: filter.startDate,
          endDate: filter.endDate,
          onlyLevel: true,
          noMain: true,
          classId: filter.classId,
          ulId: filter.ulId
        }
      )
      if (!response.data || response.error) {
        console.error(response.error)
        return
      } else {
        setByUL(response.data)
      }
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <ByStudentContext.Provider
      value={{
        curriculumId,
        currentCurriculum,
        onCurriculumChange,
        setStudentSelected,
        studentSelected,
        onLoadLearningMatrix,

        learningMatrix,
        loading,
        isLoadingUser,

        onLoadLearningGrowth,
        dataChart,

        onLoadLearningGrowthColumn,
        viewLG,
        setViewLG,

        onLoadCurriculumLevels,

        byUL,
        onLoadByUL
      }}
    >
      {children}
    </ByStudentContext.Provider>
  )
}

export const useByStudent = () => {
  const context = useContext(ByStudentContext)
  if (context === undefined) {
    throw new Error('useByStudent must be used within a ByStudentProvider')
  }
  return context
}
